import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, IndianRupee, AlertTriangle, Building2, Calendar, ArrowUpRight, ArrowDownRight, BarChart3, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/utils/api';

const MONTHS_HI = { '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec' };

function fmt(n) {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

function monthLabel(m) {
  const [y, mo] = m.split('-');
  return `${MONTHS_HI[mo]} ${y.slice(2)}`;
}

export default function RevenueForecast({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOverdue, setExpandedOverdue] = useState(false);

  useEffect(() => {
    api.get('/revenue-forecast').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-20 text-slate-400">Data load nahi hua. Refresh karein.</div>
      </Layout>
    );
  }

  const { summary, monthly_projection, overdue_risk, property_profitability } = data;
  const maxMonthly = Math.max(...monthly_projection.map(m => m.total), 1);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="revenue-forecast-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp size={24} className="text-emerald-600" />
            Revenue Forecast
          </h1>
          <p className="text-sm text-slate-500 mt-1">Cash flow projection aur risk analysis</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="forecast-summary">
          <SummaryCard label="Next 6 Months Expected" value={fmt(summary.total_expected_6_months)} icon={TrendingUp} color="#059669" bg="#f0fdf4" border="#bbf7d0" />
          <SummaryCard label="Next 30 Days" value={fmt(summary.next_30_days)} icon={Calendar} color="#2563eb" bg="#eff6ff" border="#bfdbfe" />
          <SummaryCard label="Total Overdue" value={fmt(summary.total_overdue)} icon={AlertTriangle} color="#dc2626" bg="#fef2f2" border="#fecaca" sub={`${summary.overdue_count} installments`} />
          <SummaryCard label="Active Bookings" value={summary.active_bookings} icon={BarChart3} color="#7c3aed" bg="#f5f3ff" border="#c4b5fd" />
        </div>

        {/* Monthly Projection Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar size={18} className="text-blue-600" />
              Monthly Cash Flow Projection
            </CardTitle>
            <CardDescription>Agle 6 months mein kitna paisa aane ka expected hai</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mt-2">
              {monthly_projection.map((m, i) => (
                <div key={m.month} className="group" data-testid={`month-${m.month}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-500 w-14 shrink-0">{monthLabel(m.month)}</span>
                    <div className="flex-1 h-9 bg-slate-50 rounded-lg overflow-hidden relative border">
                      {m.overdue_carry > 0 && (
                        <div className="absolute top-0 left-0 h-full bg-red-200 rounded-l-lg transition-all duration-500" style={{ width: `${(m.overdue_carry / maxMonthly) * 100}%` }} />
                      )}
                      <div
                        className="absolute top-0 h-full bg-emerald-400 transition-all duration-500 rounded-lg"
                        style={{ left: `${(m.overdue_carry / maxMonthly) * 100}%`, width: `${(m.expected / maxMonthly) * 100}%` }}
                      />
                      <div className="absolute inset-0 flex items-center px-3 justify-between text-xs">
                        <span className="font-bold text-slate-700 drop-shadow-sm">{fmt(m.total)}</span>
                        <span className="text-slate-400">{m.installment_count} installments{m.overdue_carry > 0 ? ` + overdue` : ''}</span>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 w-20 text-right shrink-0">Cumul: {fmt(m.cumulative)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Expected</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block" /> Overdue Carry</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Overdue Risk */}
          <Card className="border-red-100">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle size={18} className="text-red-500" />
                  Overdue Risk Analysis
                </CardTitle>
                <Badge variant="outline" className="text-red-600 border-red-200 text-xs">{overdue_risk.length} items</Badge>
              </div>
              <CardDescription>In installments ka payment overdue hai</CardDescription>
            </CardHeader>
            <CardContent>
              {overdue_risk.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">Koi overdue nahi hai</div>
              ) : (
                <div className="space-y-2">
                  {overdue_risk.slice(0, expandedOverdue ? 50 : 5).map((o, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border bg-white hover:bg-red-50/30 transition-colors" data-testid={`overdue-${i}`}>
                      <RiskBadge level={o.risk_level} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{o.customer_name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{o.property_name} - {o.installment_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-red-600">{fmt(o.amount)}</p>
                        <p className="text-[10px] text-red-400">{o.days_overdue} din overdue</p>
                      </div>
                    </div>
                  ))}
                  {overdue_risk.length > 5 && (
                    <button onClick={() => setExpandedOverdue(!expandedOverdue)} className="w-full text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 py-2">
                      {expandedOverdue ? <><ChevronUp size={14} /> Less</> : <><ChevronDown size={14} /> Show all {overdue_risk.length}</>}
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Property Profitability */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 size={18} className="text-indigo-600" />
                Property-wise Collection
              </CardTitle>
              <CardDescription>Property-wise revenue aur collection rate</CardDescription>
            </CardHeader>
            <CardContent>
              {property_profitability.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">Koi property data nahi hai</div>
              ) : (
                <div className="space-y-3">
                  {property_profitability.map((p, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-white" data-testid={`property-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.property_name || 'Unnamed'}</p>
                        <Badge variant="outline" className="text-xs shrink-0 ml-2">
                          {p.total_bookings} bookings
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${p.collection_rate}%`,
                              background: p.collection_rate >= 70 ? '#10b981' : p.collection_rate >= 40 ? '#f59e0b' : '#ef4444'
                            }}
                          />
                        </div>
                        <span className={`text-xs font-bold ${p.collection_rate >= 70 ? 'text-emerald-600' : p.collection_rate >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          {p.collection_rate}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-slate-400">
                        <span>Revenue: <b className="text-slate-600">{fmt(p.total_revenue)}</b></span>
                        <span>Collected: <b className="text-emerald-600">{fmt(p.total_collected)}</b></span>
                        <span>Pending: <b className="text-amber-600">{fmt(p.total_pending)}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value, icon: Icon, color, bg, border, sub }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: bg, borderColor: border }} data-testid={`summary-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="p-2 rounded-lg shrink-0" style={{ background: color }}>
        <Icon size={16} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] md:text-xs text-slate-500 font-medium truncate">{label}</p>
        <p className="text-sm md:text-lg font-bold truncate" style={{ color: '#0B1120' }}>{value}</p>
        {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  const cfg = {
    Critical: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
    High: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
    Medium: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
    Low: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  };
  const c = cfg[level] || cfg.Low;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${c.bg} ${c.text} shrink-0`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {level}
    </span>
  );
}
